import { useTu } from "@src/appV2/i18n/useTu";
import type { TuFunction } from "@src/appV2/i18n/types";
import { Box, type Theme } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";
import { InfoBanner } from "../../../components/InfoBanner/InfoBanner";
import { type InviteErrorReason } from "./types";
import { fallbackTu } from "@src/appV2/i18n/fallbackTu";

export function getErrorMessage(
  errorReasons: InviteErrorReason[],
  detail?: string,
  tu: TuFunction = fallbackTu
): string {
  if (errorReasons.length !== 1) {
    return tu("This professional cannot receive this invite due to multiple issues.");
  }

  if (errorReasons[0] === "WORKER_CANT_CLAIM_SHIFT" && detail) {
    return detail;
  }

  return getErrorMessageForReason(errorReasons[0], tu);
}

interface InviteErrorModalProps {
  modalState: UseModalState;
  errorReasons: InviteErrorReason[];
  missingDocuments?: string[];
  /** Backend detail message to display instead of the generic fallback. */
  detail?: string;
  professionalName?: string;
  qualificationName?: string;
  /**
   * When provided, the primary action lets the user pick a different professional.
   * When absent (preselected-worker flow), only a "Close" action is shown.
   */
  onSelectAnother?: () => void;
  onDismiss: () => void;
}

/**
 * Modal displayed when invite validation fails with blocking errors.
 * Shows the error reasons and prompts user to select another professional.
 */
export function InviteErrorModal(props: InviteErrorModalProps) {
  const { tu } = useTu("schedule");

  const {
    modalState,
    errorReasons,
    missingDocuments,
    detail,
    professionalName,
    qualificationName,
    onSelectAnother,
    onDismiss,
  } = props;

  const isBlockedError = errorReasons.includes("WORKER_BLOCKED");

  // Workplace/qualification issues aren't about the selected professional —
  // selecting another won't resolve them. Suppress the professional-centric
  // chrome (banner title, primary action, helper text) when that's the only
  // reason.
  const isWorkplaceQualificationIssue =
    errorReasons.length === 1 && errorReasons[0] === "QUALIFICATION_NOT_ENABLED";

  const handleSelectAnother = () => {
    modalState.closeModal();
    onSelectAnother?.();
  };

  const handleDismiss = () => {
    modalState.closeModal();
    onDismiss();
  };

  const hasMissingDocuments =
    errorReasons.includes("WORKER_MISSING_REQUIREMENTS") &&
    missingDocuments !== undefined &&
    missingDocuments.length > 0;

  const errorMessage = getErrorMessage(errorReasons, detail, tu);

  const bannerTitle = isWorkplaceQualificationIssue
    ? getWorkplaceQualificationIssueTitle({ qualificationName, tu })
    : getInviteErrorBannerTitle({ professionalName, tu });

  let footerHelperText = tu(
    "Re-enable this qualification in your workplace settings, or choose a different qualification for this shift."
  );

  if (!isWorkplaceQualificationIssue) {
    footerHelperText = onSelectAnother
      ? tu("Please select another professional to invite.")
      : tu("This professional cannot receive this invite.");
  }

  return (
    <ResponsiveDialog
      dismissible={false}
      modalState={modalState}
      showCloseButton={!isBlockedError}
      title={tu("Unable to send invite")}
      footerContent={
        isWorkplaceQualificationIssue || !onSelectAnother ? (
          <DialogFooter
            layout="horizontal"
            primaryAction={{
              label: tu("Close"),
              onClick: handleDismiss,
            }}
          />
        ) : (
          <DialogFooter
            layout="horizontal"
            primaryAction={{
              label: tu("Select another professional"),
              onClick: handleSelectAnother,
            }}
            secondaryAction={
              isBlockedError
                ? undefined
                : {
                    label: tu("Close"),
                    onClick: handleDismiss,
                  }
            }
          />
        )
      }
      onClose={isBlockedError ? () => {} : handleDismiss}
    >
      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(4),
        })}
      >
        <InfoBanner message={errorMessage} title={bannerTitle} variant="error" />

        {hasMissingDocuments && (
          <Box
            sx={(theme: Theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(1),
            })}
          >
            <Text semibold sx={(theme) => ({ color: theme.palette.text.primary })} variant="body2">
              {tu("Missing documents:")}
            </Text>
            <Box
              component="ul"
              sx={(theme: Theme) => ({
                margin: 0,
                paddingLeft: theme.spacing(4),
              })}
            >
              {missingDocuments.map((document) => (
                <Box
                  key={document}
                  component="li"
                  sx={(theme: Theme) => ({
                    color: theme.palette.text.secondary,
                  })}
                >
                  <Text variant="body2">{document}</Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {errorReasons.length > 1 && (
          <Box
            component="ul"
            sx={(theme: Theme) => ({
              margin: 0,
              paddingLeft: theme.spacing(4),
            })}
          >
            {errorReasons.map((reason) => (
              <Box
                key={reason}
                component="li"
                sx={(theme: Theme) => ({
                  color: theme.palette.text?.secondary,
                })}
              >
                <Text variant="body2">{getErrorMessageForReason(reason, tu)}</Text>
              </Box>
            ))}
          </Box>
        )}

        <Text
          sx={(theme: Theme) => ({
            color: theme.palette.text?.secondary,
          })}
          variant="body2"
        >
          {footerHelperText}
        </Text>
      </Box>
    </ResponsiveDialog>
  );
}

function getErrorMessageForReason(reason: InviteErrorReason, tu: TuFunction): string {
  switch (reason) {
    case "SHIFT_OVERWORKING":
      return tu(
        "This invite cannot be sent because the worker would exceed the mandatory 17-hour limit."
      );
    case "SHIFT_IN_PAST":
      return tu("This shift has already started.");
    case "WORKER_MISSING_LICENSE":
      return tu("This professional is missing a required license.");
    case "WORKER_MISSING_DOCUMENTS":
      return tu("This professional is missing required documents.");
    case "WORKER_MISSING_REQUIREMENTS":
      return tu("This professional is missing required documents for this shift.");
    case "WORKER_DISABLED_TEST_ACCOUNT":
      return tu("This is a disabled test account.");
    case "WORKER_CANT_CLAIM_SHIFT":
      return tu("This professional cannot claim this shift.");
    case "WORKER_PAYMENTS_DISABLED":
      return tu("This professional has payments disabled.");
    case "WORKER_PENDING_DOCUMENTS":
      return tu("This professional has documents pending review.");
    case "WORKER_UNSIGNED_AGREEMENT":
      return tu("This professional hasn't signed the required agreement.");
    case "WORKER_DEACTIVATED":
      return tu("This professional's account has been deactivated.");
    case "WORKER_BLOCKED":
      return tu("This professional has been blocked by your workplace.");
    case "FACILITY_SUSPENDED":
      return tu("This facility has been suspended.");
    case "WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK":
      return tu("This professional is missing data for background check.");
    case "WORKER_HAS_REJECTED_BACKGROUND_CHECK":
      return tu("This professional has a rejected background check.");
    case "QUALIFICATION_NOT_ENABLED":
      return tu(
        "This qualification is hidden from your workplace's calendar. Re-enable it in Settings or choose another qualification."
      );
  }
}

function getWorkplaceQualificationIssueTitle(options: {
  qualificationName: string | undefined;
  tu: TuFunction;
}): string {
  const { qualificationName, tu } = options;

  if (qualificationName === undefined) {
    return tu("Cannot post invite for this qualification");
  }

  return tu("Cannot post invite for {{qualificationName}} qualification", {
    values: { qualificationName },
  });
}

function getInviteErrorBannerTitle(options: {
  professionalName: string | undefined;
  tu: TuFunction;
}): string {
  const { professionalName, tu } = options;

  if (professionalName === undefined) {
    return tu("Unable to invite this professional");
  }

  return tu("Unable to invite {{professionalName}}", { values: { professionalName } });
}
